import { useState, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card, KpiCard } from '../components/ui/Card';
import { Panel } from '../components/ui/Panel';
import { Input } from '../components/ui/Input';
import { SectionHeader } from '../components/ui/SectionHeader';
import { RangePicker, type Range } from '../components/ui/RangePicker';
import {
  DollarSign,
  Target,
  TrendingUp,
  Globe,
  Users,
  Megaphone,
  Tag,
  Hash,
  Search,
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent } from '../lib/utils';
import { apiDownload } from '../lib/api';
import { useAuth } from '../store/auth';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubDetailDrawer } from '../components/subdetail/SubDetailDrawer';
import { usePollMultiplier } from '../hooks/usePollMultiplier';

// ─── Types ────────────────────────────────────────────────────────────────────

function utcDateInput(ts = Date.now()): string {
  return new Date(ts).toISOString().slice(0, 10);
}

const dimensions = ['Publisher', 'Sub ID', 'Campaign', 'Offer', 'Country'] as const;
type Dimension = (typeof dimensions)[number];

/** Label tampilan — "Publisher" ditampilkan sebagai "Source" agar konsisten
 *  dengan menu Traffic Sources; nilai internal tetap "Publisher" (API param). */
const dimensionLabels: Record<Dimension, string> = {
  Publisher: 'Source',
  'Sub ID': 'Sub ID',
  Campaign: 'Campaign',
  Offer: 'Offer',
  Country: 'Country',
};

interface OverviewData {
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
  profit: number;
  cr: number;
  /** P0-A — hanya terisi untuk admin global; 0/null untuk role lain. */
  epc?: number;
  adSpend?: number;
  roiPct?: number | null;
}

interface ByPublisherRow {
  publisherId: number | null;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
  /** Present for clicks-only sub_ids that aren't registered as a publisher. */
  subIdHint?: string;
}

/** Baris dimensi Sub ID (redesign Stitch): sub_id mentah dari union AE+D1. */
interface BySubidRow {
  subId: string;
  linkedPublisherId: number | null;
  linkedName: string | null;
  clicksTotal: number;
  clicksBot: number;
  botRatePct: number;
  conversionsApproved: number;
  conversionsPending: number;
  revenueGross: number;
  cr: number;
  epc: number;
}

interface ByCampaignRow {
  campaignId: number | null;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
  /** P0-A — hanya terisi untuk admin global; 0/null untuk role lain. */
  adSpend?: number;
  roiPct?: number | null;
  ecpa?: number | null;
}

interface ByOfferRow {
  offerId: number | null;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
}

interface ByCountryRow {
  country: string | null;
  clicksTotal: number;
  conversionsApproved: number;
  revenueGross: number;
}

interface Publisher {
  id: number;
  name?: string;
  displayName?: string;
  email?: string;
}

interface Campaign {
  id: number;
  displayName: string;
}

interface Offer {
  id: number;
  name: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cr(clicks: number, convs: number): string {
  if (!clicks) return '0.00%';
  return formatPercent((convs / clicks) * 100, 2);
}

function profit(rev: number, payout: number): number {
  return rev - payout;
}

// ─── Loading skeleton row ─────────────────────────────────────────────────────

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}>
          <div className="h-3 bg-bg-tertiary rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

// ─── Publisher table ──────────────────────────────────────────────────────────

function PublisherTable({
  data,
  publishers,
  isLoading,
  onRowClick,
  search,
}: {
  data: ByPublisherRow[];
  publishers: Publisher[];
  isLoading: boolean;
  /** Open the per-Sub-ID drill-down modal. Pass `null` for the unlinked
   *  group; if the row carries a `subIdHint` (unlinked-but-known-sub_id),
   *  also pass that so the modal can scope to that sub_id rather than the
   *  whole unlinked aggregate. */
  onRowClick?: (publisherId: number | null, subIdHint?: string) => void;
  search: string;
}) {
  const nameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of publishers) {
      m.set(p.id, p.displayName ?? p.name ?? p.email ?? `#${p.id}`);
    }
    return m;
  }, [publishers]);

  // Client-side filter. Keeps the row when search is empty or any matchable
  // string (display name, sub_id hint, "#id") contains the query (case-insensitive).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const label =
        row.publisherId != null
          ? (nameMap.get(row.publisherId) ?? `#${row.publisherId}`)
          : (row.subIdHint ?? 'unlinked');
      return (
        label.toLowerCase().includes(q) ||
        String(row.publisherId ?? '').includes(q)
      );
    });
  }, [data, nameMap, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Publisher</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Quality</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right" title="Yang dibayar network ke kita">
              Revenue
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={6} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => {
              // Three label cases:
              //   1. Registered publisher → display name from publishers table
              //   2. Unregistered click-only sub_id → plain sub_id text (white)
              //   3. NULL-publisher conversions (orphan) → "Unlinked" italic
              const name =
                row.publisherId != null
                  ? (nameMap.get(row.publisherId) ?? `#${row.publisherId}`)
                  : row.subIdHint
                    ? row.subIdHint
                    : 'Unlinked';
              const isUnregistered = row.publisherId == null && !!row.subIdHint;
              return (
                <tr
                  key={i}
                  onClick={
                    onRowClick
                      ? () => onRowClick(row.publisherId ?? null, row.subIdHint)
                      : undefined
                  }
                  className={
                    onRowClick
                      ? 'cursor-pointer hover:bg-neon-magenta/5'
                      : undefined
                  }
                  title={
                    isUnregistered
                      ? `Unregistered sub_id: ${row.subIdHint} (clicks-only, no publisher record)`
                      : onRowClick
                        ? 'Click to see detailed breakdown'
                        : undefined
                  }
                >
                  <td
                    className={`font-mono text-xs ${
                      isUnregistered ? 'text-text-primary' : 'text-neon-cyan'
                    }`}
                  >
                    {name}
                  </td>
                  <td className="text-right numeric">
                    {formatNumber(row.clicksTotal)}
                  </td>
                  <td className="text-right numeric text-neon-cyan">
                    {formatNumber(row.clicksQuality)}
                  </td>
                  <td className="text-right numeric text-neon-magenta">
                    {formatNumber(row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-green">
                    {cr(row.clicksQuality, row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-yellow">
                    {formatCurrency(row.revenueGross)}
                  </td>
                </tr>
              );
            })}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {/* Footer with totals — sums across whatever the current filter shows.
            When user types a search query, totals update to reflect ONLY the
            matched rows (the user's whole point of searching). Empty search =
            totals for all visible rows. Skipped during loading and empty state. */}
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={6}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.clicksQuality, 0),
                format: 'number',
                accent: 'text-neon-cyan',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── TotalsFooter — reusable footer for any dimension table ────────────────
// Renders "TOTAL · N rows (M filtered)" label + numeric cells matching the
// table's column structure. Cell values come pre-computed from the parent
// (so filtering logic stays close to the data).
function TotalsFooter({
  colSpan,
  search,
  totalRows,
  allRows,
  cells,
}: {
  colSpan: number;
  search: string;
  totalRows: number;
  allRows: number;
  cells: Array<{
    value: number | string;
    format: 'number' | 'currency' | 'literal';
    accent: string;
  }>;
}) {
  return (
    <tfoot>
      <tr className="border-t-2 border-border-subtle bg-bg-tertiary/30">
        <td className="font-display text-[10px] uppercase tracking-wider text-text-secondary py-2">
          TOTAL
          {search
            ? ` · ${totalRows} of ${allRows} (filtered)`
            : ` · ${totalRows} rows`}
        </td>
        {cells.map((c, i) => (
          <td key={i} className={`text-right numeric font-mono ${c.accent}`}>
            {c.format === 'literal'
              ? c.value
              : c.format === 'currency'
                ? formatCurrency(typeof c.value === 'number' ? c.value : 0)
                : formatNumber(typeof c.value === 'number' ? c.value : 0)}
          </td>
        ))}
      </tr>
    </tfoot>
  );
}

// ─── Campaign table ───────────────────────────────────────────────────────────

function CampaignTable({
  data,
  campaigns,
  isLoading,
  search,
}: {
  data: ByCampaignRow[];
  campaigns: Campaign[];
  isLoading: boolean;
  search: string;
}) {
  // P0-A: kolom Cost/ROI hanya admin — biaya trafik bukan view publisher.
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const colCount = isAdmin ? 8 : 6;
  const nameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of campaigns) m.set(c.id, c.displayName);
    return m;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const name =
        row.campaignId != null
          ? (nameMap.get(row.campaignId) ?? `#${row.campaignId}`)
          : '—';
      return (
        name.toLowerCase().includes(q) ||
        String(row.campaignId ?? '').includes(q)
      );
    });
  }, [data, nameMap, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Campaign</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Quality</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right" title="Yang dibayar network ke kita">
              Revenue
            </th>
            {isAdmin && (
              <>
                <th className="text-right">Cost</th>
                <th className="text-right">ROI</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={colCount} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => {
              const name =
                row.campaignId != null
                  ? (nameMap.get(row.campaignId) ?? `#${row.campaignId}`)
                  : '—';
              return (
                <tr key={i}>
                  <td className="font-display text-xs">{name}</td>
                  <td className="text-right numeric">
                    {formatNumber(row.clicksTotal)}
                  </td>
                  <td className="text-right numeric text-neon-cyan">
                    {formatNumber(row.clicksQuality)}
                  </td>
                  <td className="text-right numeric text-neon-magenta">
                    {formatNumber(row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-green">
                    {cr(row.clicksQuality, row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-yellow">
                    {formatCurrency(row.revenueGross)}
                  </td>
                  {isAdmin && (
                    <>
                      <td className="text-right numeric text-neon-orange">
                        {formatCurrency(row.adSpend ?? 0)}
                      </td>
                      <td
                        className={`text-right numeric ${
                          row.roiPct == null
                            ? 'text-text-tertiary'
                            : row.roiPct >= 0
                              ? 'text-neon-green'
                              : 'text-neon-red'
                        }`}
                      >
                        {row.roiPct == null ? '—' : formatPercent(row.roiPct, 1)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={colCount}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={colCount}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.clicksQuality, 0),
                format: 'number',
                accent: 'text-neon-cyan',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── Offer table ──────────────────────────────────────────────────────────────

function OfferTable({
  data,
  offers,
  isLoading,
  search,
}: {
  data: ByOfferRow[];
  offers: Offer[];
  isLoading: boolean;
  search: string;
}) {
  const nameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const o of offers) m.set(o.id, o.name);
    return m;
  }, [offers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const name =
        row.offerId != null
          ? (nameMap.get(row.offerId) ?? `#${row.offerId}`)
          : '—';
      return (
        name.toLowerCase().includes(q) || String(row.offerId ?? '').includes(q)
      );
    });
  }, [data, nameMap, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Offer</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Quality</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right" title="Yang dibayar network ke kita">
              Revenue
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={6} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => {
              const name =
                row.offerId != null
                  ? (nameMap.get(row.offerId) ?? `#${row.offerId}`)
                  : '—';
              return (
                <tr key={i}>
                  <td className="font-display text-xs">{name}</td>
                  <td className="text-right numeric">
                    {formatNumber(row.clicksTotal)}
                  </td>
                  <td className="text-right numeric text-neon-cyan">
                    {formatNumber(row.clicksQuality)}
                  </td>
                  <td className="text-right numeric text-neon-magenta">
                    {formatNumber(row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-green">
                    {cr(row.clicksQuality, row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-yellow">
                    {formatCurrency(row.revenueGross)}
                  </td>
                </tr>
              );
            })}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={6}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.clicksQuality, 0),
                format: 'number',
                accent: 'text-neon-cyan',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── Country table ────────────────────────────────────────────────────────────

// ─── SubIdTable — dimensi Sub ID (redesign Stitch layar 2) ───────────────────
// Baris = sub_id mentah (union AE blob2 × conversions.sub_id). Klik baris
// membuka SubIdDetailModal; sub_id terdaftar dapat view publisher penuh,
// yang belum terdaftar tetap dapat drill-down via subIdHint.
function SubIdTable({
  data,
  isLoading,
  search,
  onRowClick,
}: {
  data: BySubidRow[];
  isLoading: boolean;
  search: string;
  onRowClick: (subId: string, linkedPublisherId: number | null) => void;
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (row) =>
        row.subId.toLowerCase().includes(q) ||
        (row.linkedName ?? '').toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Sub ID</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Bot%</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right">EPC</th>
            <th className="text-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={7} />
            ))}
          {!isLoading &&
            filtered.map((row) => (
              <tr
                key={row.subId}
                onClick={() => onRowClick(row.subId, row.linkedPublisherId)}
                className="cursor-pointer hover:bg-neon-magenta/5"
                title="Click for detailed breakdown"
              >
                <td>
                  {row.linkedPublisherId != null ? (
                    <>
                      <span className="font-mono text-xs text-neon-cyan">
                        {row.subId}
                      </span>{' '}
                      <span
                        className="text-[9px] font-mono text-neon-green border border-neon-green/40 rounded-sm px-1 py-px align-top"
                        title="Sub ID ini terdaftar sebagai Traffic Source"
                      >
                        TS
                      </span>
                      {row.linkedName && (
                        <div className="text-[10px] text-text-tertiary">{row.linkedName}</div>
                      )}
                    </>
                  ) : (
                    <span className="font-mono text-xs text-text-primary">
                      {row.subId}
                      <span
                        className="ml-1.5 text-[9px] text-text-tertiary uppercase"
                        title="Belum terdaftar — klik dari sub_id ini tercatat tanpa pemilik"
                      >
                        unregistered
                      </span>
                    </span>
                  )}
                </td>
                <td className="text-right numeric">{formatNumber(row.clicksTotal)}</td>
                {/* Bot% adalah sinyal kualitas source tercepat: merah ≥20%
                    berarti trafik kemungkinan besar sampah. */}
                <td
                  className={`text-right numeric ${
                    row.botRatePct >= 20
                      ? 'text-neon-red'
                      : row.botRatePct >= 10
                        ? 'text-neon-yellow'
                        : 'text-text-secondary'
                  }`}
                >
                  {formatPercent(row.botRatePct, 1)}
                </td>
                <td className="text-right numeric text-neon-magenta">
                  {formatNumber(row.conversionsApproved)}
                </td>
                <td className="text-right numeric text-neon-green">
                  {formatPercent(row.cr, 2)}
                </td>
                <td className="text-right numeric">{formatCurrency(row.epc)}</td>
                <td className="text-right numeric text-neon-yellow">
                  {formatCurrency(row.revenueGross)}
                </td>
              </tr>
            ))}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={7}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no sub_ids yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CountryTable({
  data,
  isLoading,
  search,
}: {
  data: ByCountryRow[];
  isLoading: boolean;
  search: string;
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => (row.country ?? '').toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Country</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={5} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => (
              <tr key={i}>
                <td>
                  <CountryFlag iso2={row.country} showCode size={20} />
                </td>
                <td className="text-right numeric">
                  {formatNumber(row.clicksTotal)}
                </td>
                <td className="text-right numeric text-neon-magenta">
                  {formatNumber(row.conversionsApproved)}
                </td>
                <td className="text-right numeric text-neon-green">
                  {cr(row.clicksTotal, row.conversionsApproved)}
                </td>
                <td className="text-right numeric text-neon-yellow">
                  {formatCurrency(row.revenueGross)}
                </td>
              </tr>
            ))}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={5}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={5}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── Performance timeseries chart (Stitch layar 2) ───────────────────────────
// Sumber data dual-mode:
//   - today/yesterday → /stats/hourly (24 bucket per jam; kolom klik sengaja
//     tidak ada — AE hourly scan mahal, jadi chip KLIK disembunyikan)
//   - 7d/30d/custom  → /stats/timeseries (bucket per tanggal: klik DARI AE
//     daily series + konversi/revenue D1)
function PerformanceChart({
  range,
  statsParams,
}: {
  range: Range;
  statsParams: { range: string; from?: string; to?: string };
}) {
  const hourlyMode = range === 'today' || range === 'yesterday';
  type Metric = 'clicks' | 'conversions' | 'revenue';
  const availableMetrics: Metric[] = hourlyMode
    ? ['conversions', 'revenue']
    : ['clicks', 'conversions', 'revenue'];
  const [metric, setMetric] = useState<Metric>('conversions');
  // Jaga agar metric tetap valid saat pindah mode (mis. dari 7d 'clicks' → today)
  const activeMetric: Metric = availableMetrics.includes(metric)
    ? metric
    : 'conversions';

  const hourly = useQuery({
    queryKey: ['stats-hourly', JSON.stringify(statsParams)],
    queryFn: () =>
      apiClient.get<
        Array<{ hour: number; conversionsApproved: number; revenueGross: number }>
      >('/api/v1/stats/hourly', statsParams),
    enabled: hourlyMode,
    refetchInterval: range === 'today' ? 60_000 : false,
  });

  const series = useQuery({
    queryKey: ['stats-timeseries', JSON.stringify(statsParams)],
    queryFn: () =>
      apiClient.get<
        Array<{ date: string; clicks: number; conversions: number; revenue: number }>
      >('/api/v1/stats/timeseries', statsParams),
    enabled: !hourlyMode,
  });

  const isLoading = hourlyMode ? hourly.isLoading : series.isLoading;

  const rows = hourlyMode
    ? Array.from({ length: 24 }, (_, hour) => {
        const r = (hourly.data ?? []).find((x) => x.hour === hour);
        return {
          label: `${String(hour).padStart(2, '0')}`,
          clicks: 0,
          conversions: r?.conversionsApproved ?? 0,
          revenue: r?.revenueGross ?? 0,
        };
      })
    : (series.data ?? []).map((d) => ({
        // "2026-08-28" → "08/28" agar sumbu X padat di range panjang
        label: d.date.slice(5).replace('-', '/'),
        clicks: d.clicks,
        conversions: d.conversions,
        revenue: d.revenue,
      }));

  const values = rows.map((r) => r[activeMetric]);
  const max = Math.max(0.0001, ...values);

  const chipColor =
    activeMetric === 'clicks'
      ? 'bg-neon-cyan/20 border-neon-cyan text-neon-cyan'
      : activeMetric === 'conversions'
        ? 'bg-neon-magenta/20 border-neon-magenta text-neon-magenta'
        : 'bg-neon-yellow/20 border-neon-yellow text-neon-yellow';
  const barColor =
    activeMetric === 'clicks'
      ? 'bg-neon-cyan'
      : activeMetric === 'conversions'
        ? 'bg-neon-magenta'
        : 'bg-neon-yellow';

  return (
    <Panel level={1} bracket={true}>
      <div className="px-4 py-3 border-b border-border-subtle flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-display uppercase tracking-widest text-neon-cyan flex items-center gap-2">
          <TrendingUp size={14} />
          PERFORMANCE TIMESERIES
          <span className="text-[9px] font-mono text-text-tertiary normal-case tracking-normal">
            {hourlyMode ? '· per hour (UTC)' : '· per day (UTC)'}
          </span>
        </h3>
        <div className="flex items-center gap-1.5">
          {availableMetrics.map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`px-2.5 py-1 text-[10px] font-display uppercase tracking-wider rounded border transition-all ${
                activeMetric === m
                  ? chipColor
                  : 'bg-bg-tertiary border-border-subtle text-text-secondary hover:border-neon-cyan/40'
              }`}
            >
              {m === 'clicks' ? 'KLIK' : m === 'conversions' ? 'KONV' : 'REV'}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3 sm:p-4">
        {isLoading ? (
          <p className="text-center py-10 text-xs font-mono text-text-tertiary">
            LOADING SERIES...
          </p>
        ) : rows.length === 0 ? (
          <p className="text-center py-10 text-xs font-mono text-text-tertiary">
            NO DATA IN THIS RANGE
          </p>
        ) : (
          <div className="px-2 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
            <div className="flex items-end gap-[3px] h-40 border-b border-border-subtle/40 pb-px">
              {rows.map((r) => {
                const v = r[activeMetric];
                const h = ((v / max) * 100).toFixed(2);
                const tooltip =
                  activeMetric === 'revenue'
                    ? `${r.label} · ${formatCurrency(v)}`
                    : `${r.label} · ${formatNumber(v)} ${activeMetric === 'clicks' ? 'clicks' : 'convs'}`;
                return (
                  <div
                    key={r.label}
                    className="flex-1 h-full relative group min-w-[6px]"
                    title={tooltip}
                  >
                    <div
                      className={`absolute bottom-0 left-0 right-0 rounded-t-sm transition-all group-hover:opacity-80 ${barColor}`}
                      style={{ height: `${h}%`, minHeight: v > 0 ? '2px' : '0' }}
                    />
                  </div>
                );
              })}
            </div>
            {/* X-axis: 8 label merata untuk 30 hari, tiap-3-jam untuk hourly */}
            <div className="flex justify-between text-[9px] font-mono text-text-tertiary mt-1 px-1">
              {(hourlyMode
                ? [0, 3, 6, 9, 12, 15, 18, 21].map(String)
                : rows
                    .filter((_, i) => i % Math.max(1, Math.ceil(rows.length / 8)) === 0)
                    .map((r) => r.label)
              ).map((l) => (
                <span key={l}>{l}</span>
              ))}
              {hourlyMode && <span>24</span>}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StatisticsPage() {
  // NOTE: reverted user-role auto-scoping per user request. Statistics is
  // a network-wide view for both admin and publisher — same data, same
  // dimensions, same default. /earnings is the dedicated personal view.
  const [range, setRange] = useState<Range>('today');
  const [customFrom, setCustomFrom] = useState(() => utcDateInput());
  const [customTo, setCustomTo] = useState(() => utcDateInput());
  const [dimension, setDimension] = useState<Dimension>('Publisher');
  // Single search box — filters whichever dimension table is currently shown.
  // Reset to empty when switching dimensions so users don't see an unexpected
  // "no match" message after toggling.
  const [search, setSearch] = useState('');
  // CSV export (P2-H) — unduh laporan dimensi aktif via /stats/export.
  const [exporting, setExporting] = useState(false);
  // Noise gate khusus dimensi Sub ID (high-cardinality): sembunyikan sub_id
  // dengan klik di bawah ambang. Chips ≥10/≥50/≥100 di header tabel.
  const [minClicks, setMinClicks] = useState(10);
  // Modal target: `undefined` = closed. Object holds the (publisherId,
  // optional subIdHint) tuple so we can scope unlinked drill-downs to a
  // specific sub_id instead of aggregating all unlinked conversions.
  const [drilldownTarget, setDrilldownTarget] = useState<
    { publisherId: number | null; subIdHint?: string } | undefined
  >(undefined);
  const pollMult = usePollMultiplier();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const customValid = customFrom <= customTo;
  const statsParams =
    range === 'custom' ? { range, from: customFrom, to: customTo } : { range };
  const rangeKey =
    range === 'custom' ? `${range}:${customFrom}:${customTo}` : range;

  // Overview KPIs
  const overview = useQuery({
    queryKey: ['stats-overview', rangeKey],
    queryFn: () =>
      apiClient.get<OverviewData>('/api/v1/stats/overview', statsParams),
    refetchInterval: range === 'today' ? 15_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // Dimension-specific data.
  // BUGFIX: previously no refetchInterval — Statistics tables stayed frozen
  // until user changed range. Now auto-refresh on `today` at 15s cadence
  // (slightly slower than KPI cards' 15s to spread API load).
  // Each query is enabled only when its dimension is active so we don't burn
  // API quota refreshing 4 endpoints when only 1 is visible.
  const todayRefetch = range === 'today' ? 15_000 * pollMult : false;

  const byPublisher = useQuery({
    queryKey: ['stats-by-publisher', rangeKey],
    queryFn: () =>
      apiClient.get<ByPublisherRow[]>(
        '/api/v1/stats/by-publisher',
        statsParams,
      ),
    enabled: dimension === 'Publisher' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Publisher' ? todayRefetch : false,
  });

  const bySubid = useQuery({
    queryKey: ['stats-by-subid', rangeKey, minClicks],
    queryFn: () =>
      apiClient.get<BySubidRow[]>('/api/v1/stats/by-subid', {
        ...statsParams,
        min_clicks: minClicks,
        limit: 200,
      }),
    enabled: dimension === 'Sub ID' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Sub ID' ? todayRefetch : false,
  });

  const byCampaign = useQuery({
    queryKey: ['stats-by-campaign', rangeKey],
    queryFn: () =>
      apiClient.get<ByCampaignRow[]>('/api/v1/stats/by-campaign', statsParams),
    enabled: dimension === 'Campaign' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Campaign' ? todayRefetch : false,
  });

  const byOffer = useQuery({
    queryKey: ['stats-by-offer', rangeKey],
    queryFn: () =>
      apiClient.get<ByOfferRow[]>('/api/v1/stats/by-offer', statsParams),
    enabled: dimension === 'Offer' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Offer' ? todayRefetch : false,
  });

  const byCountry = useQuery({
    queryKey: ['stats-by-country', rangeKey],
    queryFn: () =>
      apiClient.get<ByCountryRow[]>('/api/v1/stats/by-country', statsParams),
    enabled: dimension === 'Country' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Country' ? todayRefetch : false,
  });

  // Name resolution
  // BUGFIX: was hitting /api/v1/{publishers,campaigns,offers} which are
  // admin-only. User role got 403 → name maps stayed empty → tables showed
  // "#4" instead of publisher names. Switched to /api/v1/lookups/* which
  // returns the minimal display fields needed (id + name/displayName) and
  // is accessible to both admin and user roles.
  const publishers = useQuery({
    queryKey: ['publishers-lookup'],
    queryFn: () => apiClient.get<Publisher[]>('/api/v1/lookups/publishers'),
    staleTime: 5 * 60_000,
  });

  const campaigns = useQuery({
    queryKey: ['campaigns-lookup'],
    queryFn: () => apiClient.get<Campaign[]>('/api/v1/lookups/campaigns'),
    staleTime: 5 * 60_000,
  });

  const offers = useQuery({
    queryKey: ['offers-lookup'],
    queryFn: () => apiClient.get<Offer[]>('/api/v1/lookups/offers'),
    staleTime: 5 * 60_000,
  });

  const kpi = overview.data ?? {
    clicksTotal: 0,
    clicksQuality: 0,
    conversionsApproved: 0,
    revenueGross: 0,
    payoutGross: 0,
    profit: 0,
    cr: 0,
  };

  const tableLoading =
    dimension === 'Publisher'
      ? byPublisher.isLoading
      : dimension === 'Campaign'
        ? byCampaign.isLoading
        : dimension === 'Offer'
          ? byOffer.isLoading
          : byCountry.isLoading;

  const dimIcon: Record<Dimension, ReactNode> = {
    Publisher: <Users size={14} />,
    'Sub ID': <Hash size={14} />,
    Campaign: <Megaphone size={14} />,
    Offer: <Tag size={14} />,
    Country: <Globe size={14} />,
  };

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Page Header */}
      <SectionHeader
        title={isAdmin ? 'Statistics' : 'My Statistics'}
        subtitle={`network aggregate by dimension · ${range}`}
        actions={
          <div className="flex flex-col sm:items-end gap-2">
            {/* Export CSV ghost button — only for non-Sub ID dimensions */}
            <button
              onClick={async () => {
                const reportMap = {
                  Publisher: 'by-publisher',
                  'Sub ID': 'by-subid',
                  Campaign: 'by-campaign',
                  Offer: 'by-offer',
                  Country: 'by-country',
                } as const;
                const qs = new URLSearchParams();
                qs.set('report', reportMap[dimension]);
                for (const [k, v] of Object.entries(statsParams)) {
                  if (v != null) qs.set(k, String(v));
                }
                setExporting(true);
                try {
                  await apiDownload(
                    `/api/v1/stats/export?${qs.toString()}`,
                    `rj-stats-${reportMap[dimension]}-${range}.csv`,
                  );
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded border transition-all min-w-[96px] text-right inline-block ${
                exporting
                  ? 'opacity-40 cursor-not-allowed border-border-subtle text-text-tertiary'
                  : 'border-neon-magenta/40 text-neon-magenta hover:bg-neon-magenta/10'
              }`}
            >
              {exporting ? '…' : 'export csv'}
            </button>
            {/* Range pills - wrap di layar sempit agar tidak terpotong */}
            <div className="flex flex-wrap items-center gap-3">
              <RangePicker value={range} onChange={setRange} />
              {range === 'custom' && (
                <div className="flex items-center gap-2 text-xs font-mono text-text-secondary">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="input-cyber h-8 w-32"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="input-cyber h-8 w-32"
                  />
                  {!customValid && (
                    <span className="text-neon-red">invalid range</span>
                  )}
                </div>
              )}
            </div>
            {!isAdmin && (
              <p className="text-[11px] font-mono text-neon-cyan/70">
                showing network-wide trends · filter to your own traffic on{' '}
                <a href="/earnings" className="underline hover:text-neon-cyan">
                  My Earnings
                </a>
                .
              </p>
            )}
          </div>
        }
      />

      {/* KPI overview — strip 6 metrik (Stitch layar 2). Admin melihat
          biaya/ROI; publisher melihat kualitas + EPC sebagai gantinya. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
            <Users size={12} />
            clicks total
          </div>
          <div className="text-2xl font-display font-bold numeric text-neon-cyan mt-1">
            {formatNumber(kpi.clicksTotal)}
          </div>
        </div>
        <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
            <Target size={12} />
            conversions
          </div>
          <div className="text-2xl font-display font-bold numeric text-neon-magenta mt-1">
            {formatNumber(kpi.conversionsApproved)}
          </div>
        </div>
        <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary">cr</div>
          <div className="text-2xl font-display font-bold numeric text-neon-green mt-1">
            {formatPercent(kpi.cr, 2)}
          </div>
        </div>
        <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
            <DollarSign size={12} />
            revenue gross
          </div>
          <div className="text-2xl font-display font-bold numeric text-neon-yellow mt-1">
            {formatCurrency(kpi.revenueGross)}
          </div>
        </div>
        {isAdmin ? (
          <>
            <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
              <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary">ad cost</div>
              <div className="text-2xl font-display font-bold numeric text-neon-orange mt-1">
                {formatCurrency(kpi.adSpend ?? 0)}
              </div>
            </div>
            <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
              <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary">roi</div>
              <div
                className={`text-2xl font-display font-bold numeric mt-1 ${
                  kpi.roiPct == null ? 'text-text-tertiary' : kpi.roiPct >= 0 ? 'text-neon-green' : 'text-neon-red'
                }`}
              >
                {kpi.roiPct == null ? '—' : formatPercent(kpi.roiPct, 1)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
              <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary">epc</div>
              <div className="text-2xl font-display font-bold numeric text-text-primary mt-1">
                {formatCurrency(kpi.epc ?? (kpi.clicksTotal > 0 ? kpi.revenueGross / kpi.clicksTotal : 0))}
              </div>
            </div>
            <div className="p-3.5 bg-bg-tertiary border border-border-subtle rounded">
              <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary">
                quality clicks
              </div>
              <div className="text-2xl font-display font-bold numeric text-neon-cyan mt-1">
                {formatNumber(kpi.clicksQuality)}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Performance timeseries chart — panel utama Stitch layar 2 */}
      <PerformanceChart range={range} statsParams={statsParams} />

      {/* Breakdown table - Card wrapper with Panel-based header */}
      <Card className="p-0 border-none bg-transparent">
        {/* Card header with dimension toggle + search */}
        <div className="px-5 py-4 border-b border-border-subtle flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h3 className="font-display text-xs uppercase tracking-widest text-neon-cyan flex items-center gap-2 shrink-0">
            {dimIcon[dimension]}
            breakdown • {dimensionLabels[dimension].toLowerCase()}
          </h3>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end flex-1 min-w-0">
            {/* Search — filters the currently-active dimension table */}
            <div className="w-full sm:w-64">
              <Input
                placeholder={`search ${dimensionLabels[dimension].toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<Search size={14} />}
              />
            </div>
            {/* Min-clicks gate — only for Sub ID dimension (noise filter) */}
            {dimension === 'Sub ID' && (
              <div className="flex flex-wrap bg-bg-tertiary border border-border-subtle rounded-sm p-0.5 gap-0.5">
                {[10, 50, 100].map((mc) => (
                  <button
                    key={mc}
                    onClick={() => setMinClicks(mc)}
                    className={`px-2.5 py-1 text-[10px] font-mono rounded-sm transition-all ${
                      minClicks === mc
                        ? 'bg-neon-magenta/10 text-neon-magenta border border-neon-magenta/30'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    ≥{mc} clicks
                  </button>
                ))}
              </div>
            )}
            {/* Dimension pill toggle */}
            <div className="flex flex-wrap bg-bg-tertiary border border-border-subtle rounded-sm p-0.5 gap-0.5">
              {dimensions.map((d) => (
                <button
                  key={d}
                  onClick={() => {
                    setDimension(d);
                    setSearch('');
                  }}
                  className={`px-3 py-1 text-[11px] font-display uppercase tracking-wider rounded-sm transition-all ${
                    dimension === d
                      ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/30'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {dimensionLabels[d]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table body — swaps by dimension */}
        {dimension === 'Publisher' && (
          <PublisherTable
            data={byPublisher.data ?? []}
            publishers={publishers.data ?? []}
            isLoading={tableLoading}
            onRowClick={(id, subIdHint) =>
              setDrilldownTarget({ publisherId: id, subIdHint })
            }
            search={search}
          />
        )}
        {dimension === 'Sub ID' && (
          <SubIdTable
            data={bySubid.data ?? []}
            isLoading={tableLoading}
            search={search}
            onRowClick={(subId, linkedPublisherId) =>
              setDrilldownTarget({ publisherId: linkedPublisherId, subIdHint: subId })
            }
          />
        )}
        {dimension === 'Campaign' && (
          <CampaignTable
            data={byCampaign.data ?? []}
            campaigns={campaigns.data ?? []}
            isLoading={tableLoading}
            search={search}
          />
        )}
        {dimension === 'Offer' && (
          <OfferTable
            data={byOffer.data ?? []}
            offers={offers.data ?? []}
            isLoading={tableLoading}
            search={search}
          />
        )}
        {dimension === 'Country' && (
          <CountryTable
            data={byCountry.data ?? []}
            isLoading={tableLoading}
            search={search}
          />
        )}
      </Card>

      <SubDetailDrawer
        publisherId={drilldownTarget?.publisherId}
        subIdHint={drilldownTarget?.subIdHint}
        onClose={() => setDrilldownTarget(undefined)}
        range={range}
        customFrom={customFrom}
        customTo={customTo}
      />
    </div>
  );
}
